/**
 * Assistant — the agent loop against an OpenAI-compatible chat endpoint.
 *
 * Each user message runs rounds of: POST /chat/completions with the full
 * toolset → execute any tool_calls locally against CadApi → feed results
 * back → repeat until the model answers in text (or the round cap hits).
 * Every tool execution lands in the document's undo history like a GUI
 * action, so ⌘Z steps back through whatever the model did.
 */

import type { CadApi } from './cadApi';
import { classifyToolError, type AssistantToolbox } from './tools';
import { getSetting, setSetting } from '@core/storage/settings';

export interface AssistantSettings {
  baseUrl: string;
  apiKey: string;
  model: string;
}

export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = {
  baseUrl: 'https://api.openai.com/v1',
  apiKey: '',
  model: 'gpt-4o',
};

export interface ChatEntry {
  role: 'user' | 'assistant' | 'tool-call' | 'error';
  text: string;
}

type ContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string; detail?: 'low' | 'high' | 'auto' } };

interface ApiMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | ContentPart[] | null;
  tool_calls?: {
    id: string;
    type: 'function';
    function: { name: string; arguments: string };
  }[];
  tool_call_id?: string;
}

/** Complex builds legitimately take dozens of tool rounds. */
const DEFAULT_MAX_ROUNDS = 40;

const SYSTEM_PROMPT = `You are the modeling assistant inside NovaCAD, a parametric browser CAD.
World: Z-up, millimetres. The document is a timeline of immutable features (sketch, extrude, revolve, sweep, scale, mirror, transform=move, group, instance). Editing an early feature regenerates everything downstream. Every tool call is one undo step.

Method:
1. ALWAYS call list_features first on a new request to see the current design (ids, params, statuses).
2. Build with sketches → extrude/revolve/sweep/loft. Booleans: op:"cut"/"join" act on ALL earlier visible bodies. A body used by move/scale/mirror/group/pattern/mate is CONSUMED (hidden, replaced by the result) — reference the newest feature in a chain. PREFER the purpose-built tools: primitive (box/cylinder/sphere/cone/torus in one call — sphere/torus exist ONLY here), hole (bolt holes: counterbore/countersink, M-sizes), pattern (arrays, parametric count), fillet/chamfer (round/bevel edges — edge_info lists the midpoints to target), shell (hollow enclosures), coil (springs/threads), mate (assembly placement, concentric = stud-into-hole), loft (sections → hull/transition), insert_part (use another project as a component). Sketch macros polygon/slot/rounded_rect/star/gear beat hand-computed vertices (gear = exact involute; center distance for meshing = (z1+z2)·module/2). add_sketch_entities appends to a sketch; pattern_sketch_entities clones in-sketch (bolt circles: 1 circle → circular ×6).
3. Sketch profiles: closed loops; a loop inside another is a hole; profile:0 is the largest. Datum planes XY/YZ/XZ + offset; {kind:"face",face:{$feature:<extrudeId>,path:"face:top"}} attaches to an extrude cap and rides it parametrically.
3c. Sketch on an existing body's face with plane {kind:"on_face",bodyId,near:[x,y,z]} (or faceIndex from face_info) — the easiest way to add a boss/pocket on top of a body. Wrap risky multi-step builds in checkpoint()→…→rollback(token) to bail out cleanly. create_project/switch_project build reusable parts, then insert_part assembles them; delete_project removes one (irreversible — only when the user clearly wants it gone).
3b. LOOK BEFORE YOU LEAP — cheap queries beat failed features: scene_info (one call: all visible bodies + bboxes + volumes + parameter table + errors), sketch_info (closed profiles + open-chain endpoints; call it whenever an extrude misbehaves), face_info (planar centroids/normals for hole points and sketch targets; cylinder axes for concentric mates), edge_info (fillet/chamfer handles), mass_props (exact volume/CoM), distance (exact clearance + closest points), interference (overlap check), cross_section (2D cut outline at a plane — confirm internal shape without a screenshot), silhouette_outline (occlusion-correct 2D view from any direction — the headless way to "look" at the model), measure_feature (read back a hole's actual ⌀+depth, fillet radius, edge length near a point), topology_summary (face/edge census by kind — did that boolean really merge into 1 solid?), wall_thickness (thinnest walls + where — run after shell/cut for printability), dry_run (trial any timeline tool and auto-rollback — probe a risky fillet/boolean before committing it). VERIFY assemblies with assembly_clash (every pair: interference + clearance in one call) before declaring parts fit. Tool errors may carry {code, suggestion} — follow the suggestion instead of retrying blind.
4. PARAMETRIC design: set_parameter("width","60"), then pass {"$expr":"width/2"} anywhere a number goes (feature params, sketch constraint values) — editing the parameter regenerates everything using it. Sketches accept a constraints list (see update_feature) solved at regeneration.
4b. MATERIALS: set_material(bodyId, material) gives a body real appearance AND physics — list_materials shows the library (pla, abs, petg, aluminum-6061, steel-mild, stainless-304, brass, titanium-gr5, acrylic, glass…). Assign one per part when the user names a material or asks about weight/strength; mass then appears in scene_info/mass_props (grams), and material_info returns E/ν/density/yield for engineering questions. Cheap (never regenerates geometry).
4c. FEA: run_fea(bodyId, fixtures, loads) answers "will it break/bend?" — fixture faces held, loads in N (facePoints from face_info), returns max von Mises MPa + max deflection mm + FACTOR OF SAFETY + hotspot, and paints the stress ramp on the body (clear_fea removes it). Needs a material on the body. FoS < 1 means it yields — suggest thickening near the hotspot or a stronger material, then re-run to confirm. The solve takes seconds; prefer default resolution first.
5. Prefer run_js for repetitive or computed construction (loops, math). If a capability is missing or a pattern is reusable, define_tool it — and publish_tool when the user would want a GUI button for it.
6. You can SEE: call view_model to receive a screenshot of the viewport (vision models only). Use it to verify results visually after building and to understand existing geometry.
7. After building, call fit_view. Report what you made briefly, with feature names. If a tool errors, read the message, fix your parameters, retry — don't give up silently.`;

export class Assistant {
  history: ChatEntry[] = [];
  busy = false;
  settings: AssistantSettings = { ...DEFAULT_ASSISTANT_SETTINGS };
  onChange: (() => void) | null = null;
  /** Composer toggle: attach a viewport screenshot to outgoing messages. */
  attachView = false;
  /** Text of the in-flight streamed reply (shown live in the panel). */
  streamingText: string | null = null;
  /** Current agent round while busy (panel shows progress). */
  busyRound = 0;
  /** Round cap per user message (overridable, mostly for tests). */
  maxRounds = DEFAULT_MAX_ROUNDS;

  private messages: ApiMessage[] = [{ role: 'system', content: SYSTEM_PROMPT }];
  /** Screenshots produced by view_model this round, delivered as a vision message. */
  private pendingImages: { url: string; label: string }[] = [];

  constructor(
    private readonly api: CadApi,
    private readonly toolbox: AssistantToolbox,
  ) {
    getSetting<AssistantSettings>('assistant', DEFAULT_ASSISTANT_SETTINGS)
      .then((s) => {
        this.settings = { ...DEFAULT_ASSISTANT_SETTINGS, ...s };
        this.onChange?.();
      })
      .catch(() => undefined);
  }

  async saveSettings(): Promise<void> {
    await setSetting('assistant', this.settings);
  }

  clear(): void {
    this.history = [];
    this.messages = [{ role: 'system', content: SYSTEM_PROMPT }];
    this.onChange?.();
  }

  async send(text: string): Promise<void> {
    if (this.busy || !text.trim()) return;
    this.busy = true;
    this.history.push({ role: 'user', text });

    // Optionally attach the current view to the user's message (vision).
    let userContent: string | ContentPart[] = text;
    if (this.attachView) {
      const shot = await this.api.captureView().catch(() => null);
      if (shot) {
        userContent = [
          { type: 'text', text },
          { type: 'image_url', image_url: { url: shot, detail: 'auto' } },
        ];
        this.history.push({ role: 'tool-call', text: '📷 attached current view' });
      }
    }
    this.messages.push({ role: 'user', content: userContent });
    this.onChange?.();

    try {
      let finished = false;
      let emptyReplies = 0;
      for (let round = 0; round < this.maxRounds; round++) {
        this.busyRound = round + 1;
        this.onChange?.();
        const { message, finishReason } = await this.complete();
        if (message.tool_calls?.length) {
          this.messages.push(message);
          for (const call of message.tool_calls) {
            const result = await this.executeTool(
              call.function.name,
              call.function.arguments,
            );
            this.messages.push({
              role: 'tool',
              tool_call_id: call.id,
              content: result.slice(0, 8000),
            });
          }
          // Tool messages must be text — screenshots ride along as a
          // follow-up user message with image parts (the standard pattern
          // OpenAI-compatible vision endpoints accept).
          if (this.pendingImages.length > 0) {
            this.messages.push({
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: `[view_model result — images in order: ${this.pendingImages
                    .map((p) => p.label)
                    .join(', ')}]`,
                },
                ...this.pendingImages.map(
                  (p): ContentPart => ({
                    type: 'image_url',
                    image_url: { url: p.url, detail: 'auto' },
                  }),
                ),
              ],
            });
            this.pendingImages = [];
          }
          continue;
        }
        const textOut =
          typeof message.content === 'string'
            ? message.content
            : (message.content ?? [])
                .map((p) => (p.type === 'text' ? p.text : '[image]'))
                .join('\n');

        // Empty reply (no text, no tool calls) — a provider glitch, most
        // often right after an image message. It is NOT a final answer:
        // nudge the model to continue instead of ending the build.
        if (!textOut.trim()) {
          if (++emptyReplies <= 3) {
            this.messages.push({
              role: 'user',
              content:
                'Your last reply was empty. If the build is finished, summarize what you made; otherwise continue with tool calls.',
            });
            this.history.push({
              role: 'tool-call',
              text: '⚠ empty reply from the model — asking it to continue',
            });
            this.onChange?.();
            continue;
          }
          this.history.push({
            role: 'error',
            text: 'The endpoint returned an empty reply 4 times in a row — send "continue" to retry, or check the model/endpoint in setup.',
          });
          finished = true;
          break;
        }

        // Reply cut off by the output-token limit — keep the partial text
        // and let the model finish rather than presenting it as complete.
        if (finishReason === 'length') {
          this.messages.push({ role: 'assistant', content: textOut });
          this.history.push({ role: 'assistant', text: textOut });
          this.messages.push({
            role: 'user',
            content: '(your reply hit the output token limit — continue from where it cut off)',
          });
          this.history.push({
            role: 'tool-call',
            text: '⚠ reply truncated by token limit — continuing',
          });
          this.onChange?.();
          continue;
        }

        this.messages.push({ role: 'assistant', content: textOut });
        this.history.push({ role: 'assistant', text: textOut });
        finished = true;
        break;
      }
      // NEVER stop silently: if the round cap hit mid-build, say so — the
      // conversation state is intact, so "continue" resumes right here.
      if (!finished) {
        this.history.push({
          role: 'error',
          text: `Paused after ${this.maxRounds} tool rounds without a final answer — send "continue" to keep going.`,
        });
      }
    } catch (err) {
      this.history.push({
        role: 'error',
        text: err instanceof Error ? err.message : String(err),
      });
    } finally {
      this.busy = false;
      this.busyRound = 0;
      this.onChange?.();
    }
  }

  private async executeTool(name: string, rawArgs: string): Promise<string> {
    let args: Record<string, unknown> = {};
    try {
      args = rawArgs ? (JSON.parse(rawArgs) as Record<string, unknown>) : {};
    } catch {
      return JSON.stringify({ error: 'arguments were not valid JSON' });
    }
    const summary = `${name}(${rawArgs.length > 120 ? rawArgs.slice(0, 120) + '…' : rawArgs})`;
    this.history.push({ role: 'tool-call', text: summary });
    this.onChange?.();
    try {
      // view_model is handled here: capture now, deliver the image as the
      // next user message (tool results themselves must stay text).
      if (name === 'view_model') {
        const views = Array.isArray(args.views) && args.views.length > 0
          ? (args.views as string[]).slice(0, 6)
          : [undefined];
        if (views[0] === undefined && args.fit !== false) this.api.fitView();
        for (const view of views) {
          const shot = await this.api.captureView(view);
          if (!shot) return JSON.stringify({ error: 'no viewport available to capture' });
          this.pendingImages.push({ url: shot, label: view ?? 'current view' });
        }
        this.history.push({
          role: 'tool-call',
          text: `📷 captured ${views.map((v) => v ?? 'current view').join(', ')}`,
        });
        this.onChange?.();
        return JSON.stringify({
          ok: true,
          note: `${views.length} screenshot(s) follow as an image message, in the order listed`,
        });
      }
      const tool = this.toolbox.allTools().find((t) => t.name === name);
      if (!tool) return JSON.stringify({ error: `unknown tool ${name}` });
      const result = await tool.execute(args, this.api);
      return JSON.stringify(result ?? { ok: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return JSON.stringify({ error: message, ...(classifyToolError(message) ?? {}) });
    }
  }

  /**
   * One completion round. Streams (SSE) so long generations keep bytes
   * flowing — idle-connection proxies are the usual cause of "big requests
   * time out". Falls back to plain JSON when the server ignores `stream`.
   * Retries once on transient failures; hard cap via AbortController.
   */
  private async complete(): Promise<{ message: ApiMessage; finishReason: string | null }> {
    const base = this.settings.baseUrl.replace(/\/+$/, '');
    let lastErr: unknown;
    for (let attempt = 0; attempt < 2; attempt++) {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(`${base}/chat/completions`, {
          method: 'POST',
          signal: abort.signal,
          headers: {
            'Content-Type': 'application/json',
            ...(this.settings.apiKey ? { Authorization: `Bearer ${this.settings.apiKey}` } : {}),
          },
          body: JSON.stringify({
            model: this.settings.model,
            messages: pruneForPayload(this.messages),
            tools: this.toolbox.allTools().map((t) => ({
              type: 'function',
              function: { name: t.name, description: t.description, parameters: t.parameters },
            })),
            tool_choice: 'auto',
            stream: true,
          }),
        });
        if (!res.ok) {
          const text = (await res.text()).slice(0, 300);
          // Retry only server-side hiccups; 4xx is our fault — fail fast.
          if (res.status >= 500 && attempt === 0) {
            lastErr = new Error(`${res.status}: ${text}`);
            continue;
          }
          throw new Error(`${res.status} ${res.statusText}: ${text}`);
        }

        const contentType = res.headers.get('content-type') ?? '';
        if (contentType.includes('text/event-stream') && res.body) {
          return await this.readStream(res.body);
        }
        // Server ignored `stream` — plain JSON response.
        const json = (await res.json()) as {
          choices?: { message?: ApiMessage; finish_reason?: string }[];
        };
        const message = json.choices?.[0]?.message;
        if (!message) throw new Error('Endpoint returned no message');
        return { message, finishReason: json.choices?.[0]?.finish_reason ?? null };
      } catch (err) {
        if (abort.signal.aborted) {
          throw new Error(
            `Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s — the endpoint may be overloaded or the model too slow`,
          );
        }
        // Network-level failure or dropped stream: retry once.
        if (attempt === 0 && (err instanceof TypeError || err instanceof StreamDroppedError)) {
          lastErr = err;
          continue;
        }
        throw err;
      } finally {
        clearTimeout(timer);
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  }

  /** Consume an SSE stream, surfacing text live via `streamingText`. */
  private async readStream(
    body: ReadableStream<Uint8Array>,
  ): Promise<{ message: ApiMessage; finishReason: string | null }> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const state = newStreamState();
    let buffer = '';
    this.streamingText = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const data = line.startsWith('data:') ? line.slice(5).trim() : null;
          if (!data || data === '[DONE]') continue;
          try {
            accumulateStreamChunk(state, JSON.parse(data));
          } catch {
            /* keep-alive / malformed line — skip */
          }
          if (state.content !== this.streamingText && state.content) {
            this.streamingText = state.content;
            this.onChange?.();
          }
        }
      }
    } finally {
      this.streamingText = null;
      this.onChange?.();
    }
    // No finish_reason at all means the connection dropped mid-stream —
    // whatever accumulated is untrustworthy; let the caller's retry run.
    if (state.finishReason === null && !state.content && state.toolCalls.length === 0) {
      throw new StreamDroppedError('The stream ended before the model finished — retrying');
    }
    return { message: streamStateToMessage(state), finishReason: state.finishReason };
  }
}

/** 5 minutes — long enough for big generations, finite enough to surface hangs. */
const REQUEST_TIMEOUT_MS = 300_000;

/** The SSE connection closed before the model emitted anything usable. */
class StreamDroppedError extends Error {}

// --- pure helpers (unit-tested) ------------------------------------------------

export interface StreamState {
  content: string;
  toolCalls: { id: string; name: string; args: string }[];
  /** finish_reason from the final chunk; null if the stream never finished. */
  finishReason: string | null;
}

export function newStreamState(): StreamState {
  return { content: '', toolCalls: [], finishReason: null };
}

/** Fold one SSE chunk (parsed JSON) into the accumulating message. */
export function accumulateStreamChunk(state: StreamState, chunk: unknown): void {
  const choice = (chunk as {
    choices?: { delta?: Record<string, unknown>; finish_reason?: string | null }[];
  })?.choices?.[0];
  if (choice?.finish_reason) state.finishReason = choice.finish_reason;
  const delta = choice?.delta;
  if (!delta) return;
  if (typeof delta.content === 'string') state.content += delta.content;
  const calls = delta.tool_calls as
    | { index?: number; id?: string; function?: { name?: string; arguments?: string } }[]
    | undefined;
  if (calls) {
    for (const c of calls) {
      const idx = c.index ?? 0;
      while (state.toolCalls.length <= idx) state.toolCalls.push({ id: '', name: '', args: '' });
      const slot = state.toolCalls[idx]!;
      if (c.id) slot.id = c.id;
      if (c.function?.name) slot.name += c.function.name;
      if (c.function?.arguments) slot.args += c.function.arguments;
    }
  }
}

export function streamStateToMessage(state: StreamState): {
  role: 'assistant';
  content: string | null;
  tool_calls?: { id: string; type: 'function'; function: { name: string; arguments: string } }[];
} {
  return {
    role: 'assistant',
    content: state.content || null,
    ...(state.toolCalls.length > 0
      ? {
          tool_calls: state.toolCalls.map((t, i) => ({
            id: t.id || `call_${i}`,
            type: 'function' as const,
            function: { name: t.name, arguments: t.args },
          })),
        }
      : {}),
  };
}

/**
 * Shrink the request payload: screenshots are ~35KB base64 EACH and were
 * re-sent on every round — the top cause of giant, timeout-prone requests.
 * Keep images only in the most recent two image-bearing messages; older
 * ones become a text placeholder. Also cap total history length.
 */
export function pruneForPayload<T extends { role: string; content: unknown }>(
  messages: readonly T[],
): T[] {
  const MAX_MESSAGES = 60;
  const KEEP_IMAGES = 2;
  const head = messages[0]; // system prompt
  const tail = messages.slice(1).slice(-MAX_MESSAGES);
  // The cap must not slice between an assistant tool_calls message and its
  // tool results — endpoints reject orphaned `tool` messages outright.
  while (tail.length > 0 && tail[0]!.role === 'tool') tail.shift();

  // Find the image-bearing messages, newest last.
  const imageIdxs: number[] = [];
  tail.forEach((msg, i) => {
    if (Array.isArray(msg.content) && msg.content.some((p) => p?.type === 'image_url')) {
      imageIdxs.push(i);
    }
  });
  const keep = new Set(imageIdxs.slice(-KEEP_IMAGES));

  const pruned = tail.map((msg, i) => {
    if (!Array.isArray(msg.content) || keep.has(i) || !imageIdxs.includes(i)) return msg;
    const text = (msg.content as { type: string; text?: string }[])
      .filter((p) => p.type === 'text')
      .map((p) => p.text)
      .join('\n');
    return {
      ...msg,
      content: `${text}\n[screenshot omitted — superseded by newer captures]`,
    };
  });
  return head ? [head, ...pruned] : pruned;
}
